import { Link } from 'react-router-dom';
import { useEffect } from 'react';

export default function PourquoiOffrirPage() {
  useEffect(() => {
    document.title = 'Pourquoi offrir une création artisanale en livre plié ? | Les Carnets de Lolotte';

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Découvrez pourquoi offrir une création artisanale en livre plié des Carnets de Lolotte : pièces uniques faites main, cadeaux poétiques et éco-responsables pour toutes les occasions.');
    }

    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Pourquoi offrir les créations des Carnets de Lolotte ?',
      description: 'Découvrez pourquoi offrir une création artisanale en livre plié des Carnets de Lolotte : pièces uniques faites main, cadeaux poétiques et éco-responsables pour toutes les occasions.',
      url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/pourquoi-offrir`,
      publisher: {
        '@type': 'Organization',
        name: 'Les Carnets de Lolotte',
      },
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(jsonLd);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#F8EDEA]">
      {/* En-tête */}
      <div className="bg-[#FAF9F8] py-16 sm:py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#B97A72] mb-6" style={{ fontFamily: 'Allura, cursive' }}>
            Pourquoi offrir les créations des Carnets de Lolotte ?
          </h1>
          <p className="text-base sm:text-lg text-[#6B5E5A] leading-relaxed max-w-3xl mx-auto" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Certaines occasions méritent plus qu'un objet standard. Elles appellent un cadeau porteur d'émotion, 
            de sens et d'intention. Un présent qui raconte une histoire, qui touche le cœur et qui reste gravé 
            dans les mémoires.
          </p>
        </div>
      </div>

      {/* Contenu principal */}
      <main className="py-12 sm:py-16">
        <div className="max-w-4xl mx-auto px-6">
          {/* Section 1 */}
          <section className="mb-12 bg-[#FAF9F8] rounded-2xl p-6 sm:p-8 shadow-sm hover:shadow-md transition-shadow border-2 border-[#B97A72]/20">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-[#EBD2CF] rounded-full flex-shrink-0">
                <i className="ri-hand-heart-line text-2xl text-[#B97A72]"></i>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#6B5E5A] mt-1" style={{ fontFamily: 'Playfair Display, serif' }}>
                Des créations uniques, faites à la main
              </h2>
            </div>
            <p className="text-[#6B5E5A] leading-relaxed ml-16" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Chaque pièce est réalisée artisanalement, à partir de livres pliés à la main avec patience et précision. 
              Aucune création ne se ressemble parfaitement : chaque livre a son histoire, sa texture, ses nuances. 
              Offrir une création des Carnets de Lolotte, c'est offrir une pièce unique au monde, façonnée avec soin 
              et attention.
            </p>
          </section>

          {/* Section 2 */}
          <section className="mb-12 bg-[#FAF9F8] rounded-2xl p-6 sm:p-8 shadow-sm hover:shadow-md transition-shadow border-2 border-[#B97A72]/20">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-[#EBD2CF] rounded-full flex-shrink-0">
                <i className="ri-heart-3-line text-2xl text-[#B97A72]"></i>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#6B5E5A] mt-1" style={{ fontFamily: 'Playfair Display, serif' }}>
                Offrir du sens, pas seulement un objet
              </h2>
            </div>
            <p className="text-[#6B5E5A] leading-relaxed ml-16" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Au-delà de leur beauté, ces créations portent une intention profonde. Un ange pour accompagner, 
              une bougie pour illuminer, un cœur pour témoigner de l'amour, un oiseau pour symboliser la liberté... 
              Chaque création transmet une émotion, un message : gratitude, soutien, lumière, souvenir, espoir. 
              C'est un cadeau qui parle au cœur.
            </p>
          </section>

          {/* Section 3 */}
          <section className="mb-12 bg-[#FAF9F8] rounded-2xl p-6 sm:p-8 shadow-sm hover:shadow-md transition-shadow border-2 border-[#B97A72]/20">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-[#EBD2CF] rounded-full flex-shrink-0">
                <i className="ri-time-line text-2xl text-[#B97A72]"></i>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#6B5E5A] mt-1" style={{ fontFamily: 'Playfair Display, serif' }}>
                Un cadeau qui traverse le temps
              </h2>
            </div>
            <p className="text-[#6B5E5A] leading-relaxed ml-16" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Contrairement aux présents éphémères, ces créations sont pensées pour durer. Elles ne se fanent pas, 
              ne s'usent pas avec le temps. Elles se conservent, s'exposent, se transmettent. Elles deviennent des 
              objets de mémoire, des témoins silencieux des moments importants de la vie. Un cadeau qui reste, 
              qui accompagne, qui rappelle.
            </p>
          </section>

          {/* Section 4 */}
          <section className="mb-12 bg-[#FAF9F8] rounded-2xl p-6 sm:p-8 shadow-sm hover:shadow-md transition-shadow border-2 border-[#B97A72]/20">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-[#EBD2CF] rounded-full flex-shrink-0">
                <i className="ri-leaf-line text-2xl text-[#B97A72]"></i>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#6B5E5A] mt-1" style={{ fontFamily: 'Playfair Display, serif' }}>
                Une démarche poétique et éco-responsable
              </h2>
            </div>
            <p className="text-[#6B5E5A] leading-relaxed ml-16" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Chaque création naît d'un livre recyclé, transformé avec respect et délicatesse. C'est la poésie de 
              donner une seconde vie aux livres, de transformer des pages oubliées en œuvres d'art. Une démarche 
              qui honore la matière, qui célèbre la beauté du recyclage créatif, et qui s'inscrit dans une vision 
              douce et responsable de la création.
            </p>
          </section>

          {/* Section 5 */}
          <section className="mb-12 bg-[#FAF9F8] rounded-2xl p-6 sm:p-8 shadow-sm hover:shadow-md transition-shadow border-2 border-[#B97A72]/20">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-[#EBD2CF] rounded-full flex-shrink-0">
                <i className="ri-gift-line text-2xl text-[#B97A72]"></i>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#6B5E5A] mt-1" style={{ fontFamily: 'Playfair Display, serif' }}>
                Pour toutes les occasions, toute l'année
              </h2>
            </div>
            <p className="text-[#6B5E5A] leading-relaxed ml-16" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Ces créations s'offrent pour de nombreuses occasions : un anniversaire, un remerciement, une fête, 
              une décoration d'intérieur, Noël, la Saint-Valentin, ou simplement pour faire plaisir sans raison 
              particulière. Elles s'adaptent à tous les moments de la vie où l'on souhaite exprimer quelque chose 
              de sincère et de beau.
            </p>
          </section>

          {/* Conclusion */}
          <div className="text-center mt-16 mb-12 bg-[#EBD2CF] rounded-2xl p-8 sm:p-12">
            <p className="text-base sm:text-lg text-[#6B5E5A] leading-relaxed max-w-3xl mx-auto mb-8" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Offrir une création des Carnets de Lolotte, c'est choisir un cadeau qui a une âme, qui porte une 
              intention, qui raconte une histoire. C'est offrir bien plus qu'un objet : c'est offrir une émotion, 
              un souvenir, une présence douce et lumineuse.
            </p>
            <Link 
              to="/boutique"
              className="inline-block bg-[#B97A72] text-white px-8 sm:px-10 py-3 sm:py-4 rounded-full font-semibold text-base sm:text-lg hover:bg-[#C49B86] transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Découvrir les créations
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
